import { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/TrueViewTable.utils';
import { ApiResponse } from '../types/TrueViewTable.types';

export const useTrueViewData = (appliedMonth: string, appliedYear: string) => {
  const [marketData, setMarketData] = useState<any>(null);
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [extendedEvaluationData, setExtendedEvaluationData] = useState<any>(null);
  const [extendedMarketData, setExtendedMarketData] = useState<any>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchData = async (endpoint: string, filename: string) => {
    const apiBaseUrl = getApiBaseUrl();
    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear().toString();
    
    const useMonth = appliedMonth.padStart(2, '0');
    const useYear = appliedYear || currentYear;
    
    let dateParam = '';
    if (useMonth === currentMonth && useYear === currentYear) {
      dateParam = currentDate.toISOString().split('T')[0];
    } else {
      const nextMonth = parseInt(useMonth) + 1;
      const nextYear = nextMonth > 12 ? parseInt(useYear) + 1 : parseInt(useYear);
      const adjustedMonth = nextMonth > 12 ? 1 : nextMonth;
      dateParam = `${nextYear}-${adjustedMonth.toString().padStart(2, '0')}-01`;
    }

    const response = await fetch(`${apiBaseUrl}${endpoint}?name=${filename}&date=${dateParam}`);
    return await response.json();
  };

  const fetchMarketDataExtended = async () => {
    const apiBaseUrl = getApiBaseUrl();
    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear().toString();
    
    const useMonth = appliedMonth.padStart(2, '0');
    const useYear = appliedYear || currentYear;
    
    let dateParam = '';
    if (useMonth === currentMonth && useYear === currentYear) {
      dateParam = currentDate.toISOString().split('T')[0];
    } else {
      const nextMonth = parseInt(useMonth) + 1;
      const nextYear = nextMonth > 12 ? parseInt(useYear) + 1 : parseInt(useYear);
      const adjustedMonth = nextMonth > 12 ? 1 : nextMonth;
      dateParam = `${nextYear}-${adjustedMonth.toString().padStart(2, '0')}-01`;
    }

    const response = await fetch(`${apiBaseUrl}/api/bot/trueview/market-data?date=${dateParam}`);
    return await response.json();
  };

  useEffect(() => {
    const fetchEvaluationData = async () => {
      setEvaluationLoading(true);
      try {
        const data = await fetchData('/api/bot/trueview/data', 'List+of+Evaluations.json');
        if (data.success && data.data) {
          setEvaluationData(data);
          setDataError(null);
        } else {
          setDataError('No evaluation data available');
        }
      } catch (error) {
        setDataError('Failed to fetch evaluation data');
      } finally {
        setEvaluationLoading(false);
      }
    };

    fetchEvaluationData();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    const fetchLastSuccessfulTime = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/bot/trueview/bot/status/last-successful`);
        const data = await response.json();
        if (data.success && data.data && data.data.timestamp) {
          const timestamp = new Date(data.data.timestamp);
          const adjustedTime = new Date(timestamp.getTime() - (5 * 60 * 60 * 1000));
          const formattedTime = adjustedTime.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          setLastUpdated(formattedTime);
        }
      } catch (error) {
        console.error('Failed to fetch last successful time:', error);
      }
    };

    fetchLastSuccessfulTime();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    const fetchMarketData = async () => {
      setMarketLoading(true);
      try {
        const data = await fetchData('/api/bot/trueview/data', 'market_dm_stores.json');
        if (data.success && data.data) {
          setMarketData(data);
        } else {
          setDataError('No market data available');
        }
      } catch (error) {
        setDataError('Failed to fetch market data');
      } finally {
        setMarketLoading(false);
      }
    };

    fetchMarketData();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    const fetchExtendedData = async () => {
      try {
        const data = await fetchData('/api/bot/trueview/extended/data', 'List+of+Evaluations_extended.json');
        if (data.success && data.data) {
          setExtendedEvaluationData(data);
        }
      } catch (error) {
        console.error('Failed to fetch extended evaluation data:', error);
      }
    };

    fetchExtendedData();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    const fetchExtendedMarketData = async () => {
      try {
        const data = await fetchMarketDataExtended();
        if (data.success && data.data) {
          setExtendedMarketData(data);
        }
      } catch (error) {
        console.error('Failed to fetch extended market data:', error);
      }
    };

    fetchExtendedMarketData();
  }, [appliedMonth, appliedYear]);

  return {
    marketData,
    evaluationData,
    extendedEvaluationData,
    extendedMarketData,
    dataError,
    evaluationLoading,
    marketLoading,
    lastUpdated
  };
};